import React, { useEffect, useRef, useState, useCallback } from "react";
import { Box, Text, useMantineTheme } from "@mantine/core";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { DisplayRunningMessage } from "@/utils";
import { matchesRecurrence } from "@/utils/dateHelpers";

// Initialize dayjs UTC plugin
dayjs.extend(utc);

export interface DisplayBlockRunningMessageProps {
  /** Array of display running messages to show sequentially */
  displayRunningMessages?: DisplayRunningMessage[] | null;

  /** Additional CSS class name for styling */
  className?: string;

  themeConfig?: any;
}

export const DisplayBlockRunningMessage: React.FC<
  DisplayBlockRunningMessageProps
> = ({ displayRunningMessages, className, themeConfig }) => {
  const theme = useMantineTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const animationTimeoutRef = useRef<NodeJS.Timeout>(null);
  const timeIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [containerWidth, setContainerWidth] = useState(0);
  const [textWidth, setTextWidth] = useState(0);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [animationKey, setAnimationKey] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentTime, setCurrentTime] = useState(() => dayjs());

  // Update current time every minute to check for time-based message changes
  useEffect(() => {
    timeIntervalRef.current = setInterval(() => {
      setCurrentTime(dayjs());
    }, 60000); // Update every minute

    return () => {
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
      }
    };
  }, []);

  // Filter messages based on current datetime and schedule
  const getValidMessages = useCallback(
    (checkTime: dayjs.Dayjs): DisplayRunningMessage[] => {
      if (!displayRunningMessages?.length) return [];

      // Convert checkTime to local timezone for consistent comparison
      const localCheckTime = checkTime.local();

      return displayRunningMessages.filter((displayMessage) => {
        // Skip if no running message
        if (!displayMessage.runningMessage) {
          return false;
        }

        const runningMessage = displayMessage.runningMessage;

        // Get effective start/end datetime (assignment overrides running message)
        const effectiveStartDatetime =
          displayMessage.startDatetime || runningMessage.startDatetime;
        const effectiveEndDatetime =
          displayMessage.endDatetime || runningMessage.endDatetime;

        // Check date/time range (startDatetime/endDatetime)
        if (effectiveStartDatetime || effectiveEndDatetime) {
          // If both startDatetime and endDatetime are not provided, show all the time
          if (!effectiveStartDatetime && !effectiveEndDatetime) {
            // Continue to other checks
          } else {
            // Check start datetime - convert UTC to local for comparison
            if (effectiveStartDatetime) {
              const startTime = dayjs.utc(effectiveStartDatetime).local();
              if (localCheckTime.isBefore(startTime)) {
                return false;
              }
            }

            // Check end datetime - convert UTC to local for comparison
            if (effectiveEndDatetime) {
              const endTime = dayjs.utc(effectiveEndDatetime).local();
              if (localCheckTime.isAfter(endTime)) {
                return false;
              }
            }
          }
        }

        // Check recurrence pattern (use local time for recurrence checks)
        if (
          runningMessage.recurrencePattern &&
          runningMessage.recurrencePattern !== "NONE"
        ) {
          if (
            !matchesRecurrence(
              localCheckTime,
              runningMessage.recurrencePattern,
              runningMessage.recurrenceDays
            )
          ) {
            return false;
          }
        }

        // Check daily time range (startTime/endTime)
        if (runningMessage.startTime || runningMessage.endTime) {
          // Create dayjs objects for time comparison using today's date with the specified times
          const today = localCheckTime.format("YYYY-MM-DD");
          const currentTime = localCheckTime;

          let startTime: dayjs.Dayjs | undefined;
          let endTime: dayjs.Dayjs | undefined;

          if (runningMessage.startTime) {
            // Convert UTC time to local time and create a dayjs object for today
            const startTimeUTC = dayjs.utc(runningMessage.startTime);
            const startTimeLocal = startTimeUTC.local().format("HH:mm");
            startTime = dayjs(`${today} ${startTimeLocal}`);
          }

          if (runningMessage.endTime) {
            // Convert UTC time to local time and create a dayjs object for today
            const endTimeUTC = dayjs.utc(runningMessage.endTime);
            const endTimeLocal = endTimeUTC.local().format("HH:mm");
            endTime = dayjs(`${today} ${endTimeLocal}`);
          }

          // If only startTime is provided
          if (startTime && !endTime) {
            if (currentTime.isBefore(startTime)) {
              return false;
            }
          }
          // If only endTime is provided
          else if (!startTime && endTime) {
            if (currentTime.isAfter(endTime)) {
              return false;
            }
          }
          // If both startTime and endTime are provided
          else if (startTime && endTime) {
            // Handle normal time range (e.g., 09:00 - 17:00)
            if (startTime.isBefore(endTime) || startTime.isSame(endTime)) {
              if (
                currentTime.isBefore(startTime) ||
                currentTime.isAfter(endTime)
              ) {
                return false;
              }
            }
            // Handle overnight time range (e.g., 22:00 - 06:00)
            else {
              // For overnight ranges, we need to check if current time is NOT in the "off" period
              // Off period is between endTime and startTime
              if (
                currentTime.isAfter(endTime) &&
                currentTime.isBefore(startTime)
              ) {
                return false;
              }
            }
          }
        }

        return true;
      });
    },
    [displayRunningMessages]
  );

  const validMessages = getValidMessages(currentTime);
  const currentMessage = validMessages[currentMessageIndex];
  const runningMessage = currentMessage?.runningMessage;

  // Calculate animation duration based on scroll speed
  const scrollSpeed = runningMessage?.scrollSpeed || 50;
  const animationDuration =
    containerWidth + textWidth > 0
      ? (containerWidth + textWidth) / scrollSpeed
      : 10;

  // Measure container and text dimensions
  useEffect(() => {
    const measureDimensions = () => {
      if (containerRef.current && textRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
        setTextWidth(textRef.current.offsetWidth);
      }
    };

    measureDimensions();

    // Re-measure on window resize
    window.addEventListener("resize", measureDimensions);
    return () => window.removeEventListener("resize", measureDimensions);
  }, [currentMessage?.runningMessage?.messageText]);

  // Handle message cycling
  useEffect(() => {
    if (!validMessages.length || !containerWidth || !textWidth) return;

    setIsAnimating(true);

    // Clear any existing timeout
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }

    // Set timeout to move to next message after current animation completes
    animationTimeoutRef.current = setTimeout(() => {
      setIsAnimating(false);
      const nextIndex = (currentMessageIndex + 1) % validMessages.length;
      setCurrentMessageIndex(nextIndex);
      // Increment animation key to force new animation even if index is the same (single message case)
      setAnimationKey((prev) => prev + 1);
    }, animationDuration * 1000);

    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [
    validMessages.length,
    containerWidth,
    textWidth,
    animationDuration,
    currentMessageIndex,
    animationKey,
  ]);

  // Reset message index and animation key when valid messages change
  useEffect(() => {
    setCurrentMessageIndex(0);
    setAnimationKey(0);
  }, [validMessages.length]);

  // Reset index when messages change or current message becomes invalid
  useEffect(() => {
    if (validMessages.length === 0) {
      setCurrentMessageIndex(0);
      return;
    }

    // Check if current index is out of bounds
    if (currentMessageIndex >= validMessages.length) {
      setCurrentMessageIndex(0);
      return;
    }

    // Check if the current message is still in the valid list
    const currentMsg = validMessages[currentMessageIndex];
    const currentMsgStillExists =
      currentMsg && validMessages.some((msg) => msg.id === currentMsg.id);

    if (!currentMsgStillExists && validMessages.length > 0) {
      // Current message no longer exists, reset to first available message
      setCurrentMessageIndex(0);
    }
  }, [validMessages, currentMessageIndex]);

  // Don't render if no valid messages
  if (!validMessages.length || !runningMessage) {
    return null;
  }

  // Get colors with fallbacks
  const messageText = runningMessage.messageText || runningMessage.name || "";

  // Create unique animation name using both index and key
  const animationName = `scroll-left-${currentMessageIndex}-${animationKey}`;

  return (
    <Box
      ref={containerRef}
      className={`running-message ${className || ""}`}
      style={{
        width: "100%",
        height: "50px",
        ...(runningMessage?.importance &&
          themeConfig &&
          themeConfig.runningMessages?.important && {
            ...themeConfig.runningMessages.important,
          }),
        ...(!runningMessage?.importance &&
          themeConfig &&
          themeConfig.runningMessages?.regular && {
            ...themeConfig.runningMessages.regular,
          }),
        overflow: "hidden",
        position: "relative",
        padding: `3px 0`,
        whiteSpace: "nowrap",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
      }}
    >
      <Box
        ref={textRef}
        className="running-message__text"
        style={{
          display: "inline-block",
          animation:
            containerWidth && textWidth && isAnimating
              ? `${animationName} ${animationDuration}s linear`
              : "none",
          transform: `translateX(${containerWidth}px)`,
        }}
      >
        <Text
          style={{
            color: "inherit",
            fontSize: "inherit",
            fontWeight: "inherit",
            display: "inline-block",
            paddingRight: theme.spacing.xl,
          }}
        >
          {messageText}
        </Text>
      </Box>

      <style>
        {`
          @keyframes ${animationName} {
            0% {
              transform: translateX(${containerWidth}px);
            }
            100% {
              transform: translateX(-${textWidth}px);
            }
          }
        `}
      </style>
    </Box>
  );
};
